import { Box } from "@mui/material";
import { Fragment } from "react";

import { Text } from "../components/Text";
import { type Exclusion } from "./api/useWorkerBlockedStatus";
import { ChatMessage } from "./ChatMessage";
import { ChatBlockedState } from "./components/ChatBlockedState";
import { ChatDateSeparator, shouldShowDateSeparator } from "./components/ChatDateSeparator";
import { ChatEmptyState } from "./components/ChatEmptyState";
import { ChatErrorState } from "./components/ChatErrorState";
import { ChatLoadingState } from "./components/ChatLoadingState";
import {
  type ChatMessage as ChatMessageType,
  type ReplyContext,
  type TextChatMessage,
} from "./types";
import { useTu } from "@src/appV2/i18n/useTu";

interface ChatPanelMessageContentProps {
  messages: readonly ChatMessageType[];
  workerName: string;
  workplaceId: string;
  /** Workplace user ID for FCM request message actions. */
  workplaceUserId?: string;
  /**
   * IANA time zone of the workplace this chat belongs to (e.g. "America/New_York").
   * When provided, per-message timestamps render in this time zone so they
   * match the channel list sidebar.
   */
  facilityTimeZone?: string;
  isLoading: boolean;
  isError: boolean;
  isBlocked: boolean;
  isAdmin: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  exclusion?: Exclusion;
  onRetry: () => void;
  onLoadMore: () => void;
  onReply: (context: ReplyContext) => void;
  /** Undefined disables the per-message kebab (admin / blocked viewers). */
  onMarkAsUnread?: (messageId: number) => void;
  /** Edit handler for outgoing text messages. Undefined hides the affordance. */
  onEdit?: (message: TextChatMessage) => void;
  /** Delete handler for outgoing text messages. Undefined hides the affordance. */
  onDelete?: (message: TextChatMessage) => void;
}

export function ChatPanelMessageContent(props: ChatPanelMessageContentProps) {
  const { tu } = useTu("chat");
  const {
    messages,
    workerName,
    workplaceId,
    workplaceUserId,
    facilityTimeZone,
    isLoading,
    isError,
    isBlocked,
    isAdmin,
    hasMore,
    isLoadingMore,
    exclusion,
    onRetry,
    onLoadMore,
    onReply,
    onMarkAsUnread,
    onEdit,
    onDelete,
  } = props;

  if (isLoading) {
    return <ChatLoadingState />;
  }

  if (isError) {
    return <ChatErrorState onRetry={onRetry} />;
  }

  if (isBlocked && messages.length === 0) {
    return <ChatBlockedState exclusion={exclusion} workerName={workerName} />;
  }

  if (!isBlocked && messages.length === 0) {
    return <ChatEmptyState workerName={workerName} />;
  }

  // Parent scroll container is `flex-direction: column-reverse`, so DOM order
  // is the reverse of visual order: first child = bottom, last child = top.
  // Newest messages render first (so they sit at the bottom). Date separators
  // are emitted AFTER a message in DOM — that places them ABOVE it visually,
  // which is where the day-header belongs (above the first message of a day).
  // Load-more and the blocked banner render last in DOM = top visually.
  const reversedMessages = [...messages].reverse();
  return (
    <>
      {reversedMessages.map((message, index) => {
        const isIncoming = message.senderId !== workplaceId;
        const interactionsDisabled = isAdmin || isBlocked;
        const nextOlderTimestamp =
          index < reversedMessages.length - 1 ? reversedMessages[index + 1].createdAt : undefined;
        const showSeparator = shouldShowDateSeparator(
          message.createdAt,
          nextOlderTimestamp,
          facilityTimeZone
        );

        return (
          <Fragment key={message.messageId}>
            <ChatMessage
              message={message}
              isIncoming={isIncoming}
              facilityTimeZone={facilityTimeZone}
              workplaceId={workplaceId}
              workplaceUserId={workplaceUserId}
              onReply={interactionsDisabled || !isIncoming ? undefined : onReply}
              onMarkAsUnread={interactionsDisabled ? undefined : onMarkAsUnread}
              onEdit={interactionsDisabled ? undefined : onEdit}
              onDelete={interactionsDisabled ? undefined : onDelete}
            />
            {showSeparator && (
              <ChatDateSeparator timestamp={message.createdAt} timeZone={facilityTimeZone} />
            )}
          </Fragment>
        );
      })}
      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              cursor: "pointer",
            })}
            onClick={onLoadMore}
          >
            {isLoadingMore ? "Loading..." : tu("Load more messages")}
          </Text>
        </Box>
      )}
      {isBlocked && <ChatBlockedState exclusion={exclusion} workerName={workerName} />}
    </>
  );
}
